/**
 * SharerRewardNotifier — celebrates in real-time when the current user is
 * awarded coins by a creator for sharing their content.
 *
 * Listens on the shared WebSocket for `notification_new` payloads where
 * `notification.kind === "sharer_reward"`, then pops a full-viewport
 * cartoon-dancing celebration overlay with the creator's coin amount and
 * an optional badge. Dismisses on tap or after 6 s.
 *
 * The dancing character is drawn purely with CSS keyframes (no images /
 * lottie dep) so it works offline and never fails to load.
 */
import { useEffect, useState } from "react";
import { useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";
import { X, Coins } from "lucide-react";

export default function SharerRewardNotifier() {
  const { subscribe } = useWS();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    return subscribe((payload) => {
      if (
        !payload ||
        payload.type !== "notification_new" ||
        payload.notification?.kind !== "sharer_reward"
      ) return;
      setEvent(payload.notification);
      // Auto-dismiss after 6 s so it never blocks the app.
      setTimeout(() => setEvent(null), 6000);
    });
  }, [user, subscribe]);

  if (!event) return null;
  const coins = event.amount_cents || 0;
  const actor = event.actor_name || "A creator";

  return (
    <div
      data-testid="sharer-reward-overlay"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 pointer-events-none"
    >
      <div
        onClick={() => setEvent(null)}
        className="pointer-events-auto relative w-full max-w-sm bg-[#0A0A0C] border-2 border-[#39FF14] shadow-[0_0_60px_rgba(57,255,20,0.6)] p-6 text-center animate-reward-pop"
      >
        <button
          type="button"
          onClick={() => setEvent(null)}
          className="absolute top-2 right-2 text-white/50 hover:text-white"
          aria-label="Close"
          data-testid="sharer-reward-close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Cartoon dancing character — CSS-only, no external assets. */}
        <div className="text-6xl leading-none mb-2 animate-cartoon-dance" aria-hidden="true">
          💃
        </div>

        <p
          className="font-display font-black text-2xl neon-text-green"
          data-text="THANK YOU FOR SHARING!"
        >
          THANK YOU FOR SHARING!
        </p>
        <p className="text-white/70 text-sm mt-2">
          <span className="font-bold text-white">{actor}</span> just sent you
        </p>
        <p
          data-testid="sharer-reward-amount"
          className="mt-1 font-display font-black text-4xl text-[#39FF14] flex items-center justify-center gap-2"
        >
          <Coins className="h-7 w-7" strokeWidth={2.2} />
          {coins}
          <span className="text-lg text-white/60 font-mono">coins</span>
        </p>
        {event.message && (
          <p className="mt-3 text-white/60 text-xs italic px-2">
            {event.message}
          </p>
        )}
      </div>
    </div>
  );
}
